import { motion } from 'framer-motion';
import { CircleDollarSign, RefreshCw } from 'lucide-react';
import { type MatrixNode, type NodeKind } from '../data/view-model';

// B-Dynamic is a single-leg matrix: twelve positions fill one straight line beneath the
// member, never a branching tree. The layout below is a single vertical column.
const POSITION_COUNT = 12;
const COLUMN_X = 180;
const ROOT_BOTTOM_Y = 76;
const FIRST_NODE_Y = 150;
const NODE_STEP_Y = 72;
const NODE_HALF_HEIGHT = 27;

const coordinates = Array.from(
  { length: POSITION_COUNT },
  (_, index) => [COLUMN_X, FIRST_NODE_Y + index * NODE_STEP_Y] as const,
);

const edges = coordinates.map((point, index) => {
  const fromY = index === 0 ? ROOT_BOTTOM_Y : coordinates[index - 1]![1] + NODE_HALF_HEIGHT;
  const toY = point[1] - NODE_HALF_HEIGHT;
  return [COLUMN_X, fromY, COLUMN_X, toY] as const;
});

const VIEWBOX_HEIGHT = FIRST_NODE_Y + (POSITION_COUNT - 1) * NODE_STEP_Y + 60;

const labels: Record<NodeKind, string> = {
  you: 'You',
  income: 'Your income',
  upline: 'Upline route',
  downline: 'Downline route',
  treasury: 'Upgrade / treasury',
  recycle: 'Recycle',
  empty: 'Awaiting placement',
};

const legend: { kind: NodeKind; label: string }[] = [
  { kind: 'you', label: 'You' },
  { kind: 'income', label: 'Your income' },
  { kind: 'upline', label: 'Upline' },
  { kind: 'downline', label: 'Downline' },
  { kind: 'treasury', label: 'Upgrade / treasury' },
  { kind: 'recycle', label: 'Recycle' },
  { kind: 'empty', label: 'Open position' },
];

function NodeShape({ node, x, y }: { node: MatrixNode; x: number; y: number }) {
  const isTriangle = node.kind === 'upline' || node.kind === 'downline';
  const className = `matrix-node node-${node.kind}`;
  return (
    <motion.g
      className={className}
      initial={{ opacity: 0, scale: 0.6 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.42, delay: node.position * 0.035 }}
      whileHover={{ scale: 1.08 }}
      style={{ transformOrigin: `${x}px ${y}px` }}
      tabIndex={0}
      role="group"
      aria-label={`Position ${node.position}. ${labels[node.kind]}. ${node.memberId ?? 'Empty'}`}
    >
      <title>{`P${node.position} · ${labels[node.kind]} · ${node.memberId ?? 'Open'}`}</title>
      {node.kind === 'recycle' ? (
        <>
          <circle cx={x} cy={y} r="26" />
          <foreignObject x={x - 11} y={y - 11} width="22" height="22"><RefreshCw size={22} /></foreignObject>
        </>
      ) : (
        // Every non-recycle node uses the same 68px rect: the old upline/downline triangles were
        // only ~28px wide at the label's baseline, so member IDs spilled outside the shape.
        <rect x={x - 34} y={y - 24} width="68" height="48" rx="15" />
      )}
      {isTriangle ? (
        <path
          className="node-direction"
          d={node.kind === 'upline' ? `M ${x + 24} ${y + 16} l 5 0 l -2.5 -5 Z` : `M ${x + 24} ${y - 16} l 5 0 l -2.5 5 Z`}
        />
      ) : null}
      {node.kind !== 'recycle' ? <text className="node-position" x={x} y={y - 5}>P{node.position}</text> : null}
      {node.memberId ? <text className="node-id" x={x} y={y + 12}>{node.memberId.replace('BD-', '')}</text> : node.kind !== 'recycle' ? <text className="node-empty" x={x} y={y + 12}>OPEN</text> : null}
    </motion.g>
  );
}

export function MatrixGraph({ nodes, rootId, cycle }: { nodes: MatrixNode[]; rootId: string; cycle: number }) {
  return (
    <div className="matrix-wrap">
      <div className="matrix-scroll">
        <svg viewBox={`0 0 360 ${VIEWBOX_HEIGHT}`} className="matrix-svg matrix-svg-vertical" role="img" aria-labelledby="matrix-title matrix-desc">
          <title id="matrix-title">Cycle {cycle} placement line for {rootId}</title>
          <desc id="matrix-desc">A single-leg line of twelve positions, stacked in order beneath the member. Every node has an accessible position and routing label.</desc>
          <defs>
            <linearGradient id="root-fill" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor="#b14cff" />
              <stop offset="1" stopColor="#6e5cff" />
            </linearGradient>
            {/* userSpaceOnUse is required: the edges are perfectly vertical, so their object
                bounding box has zero width and an objectBoundingBox gradient makes them vanish. */}
            <linearGradient
              id="edge-gradient"
              gradientUnits="userSpaceOnUse"
              x1={COLUMN_X}
              y1={ROOT_BOTTOM_Y}
              x2={COLUMN_X}
              y2={VIEWBOX_HEIGHT}
            >
              <stop offset="0" stopColor="#7756ec" stopOpacity=".85" />
              <stop offset=".5" stopColor="#48c9ff" stopOpacity=".9" />
              <stop offset="1" stopColor="#e15fff" stopOpacity=".55" />
            </linearGradient>
            <filter id="node-glow"><feGaussianBlur stdDeviation="5" result="blur" /><feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
          </defs>
          <g className="matrix-edges">
            {edges.map(([x1, y1, x2, y2], index) => (
              <motion.path
                key={`${x1}-${x2}-${index}`}
                d={`M ${x1} ${y1} C ${x1} ${(y1 + y2) / 2}, ${x2} ${(y1 + y2) / 2}, ${x2} ${y2}`}
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 1 }}
                transition={{ duration: 0.7, delay: index * 0.025 }}
              />
            ))}
          </g>
          <motion.g className="matrix-root" initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}>
            <rect x={COLUMN_X - 100} y="24" width="200" height="52" rx="18" />
            <foreignObject x={COLUMN_X - 85} y="40" width="18" height="18"><CircleDollarSign size={18} /></foreignObject>
            <text x={COLUMN_X - 58} y="42" className="root-label">YOU</text>
            <text x={COLUMN_X - 58} y="60" className="root-id">{rootId}</text>
          </motion.g>
          {nodes.map((node, index) => {
            const point = coordinates[index];
            return point ? <NodeShape key={node.position} node={node} x={point[0]} y={point[1]} /> : null;
          })}
        </svg>
      </div>
      <div className="matrix-legend" aria-label="Matrix legend">
        {legend.map(({ kind, label }) => <span key={kind}><i className={`legend-shape legend-${kind}`} />{label}</span>)}
      </div>
    </div>
  );
}
